// Types de messages
export const MessageTypes = {
  USER: 'user',
  ASSISTANT: 'assistant',
} as const;

// Types de fonctions que l'IA peut appeler
export const FunctionTypes = {
  ASK_FOR_DATA: 'ask_for_data',
  CLARIFY_REQUEST: 'clarify_request',
  SKIP_SLOT: 'skip_slot',
  RECOMMENDATION: 'recommendation',
  LIMIT_REACHED: 'limit_reached',
} as const;

// Schéma des slots à collecter pour l'optimisation Flashgate
export const SlotSchema = {
  primaryObjective: {
    key: 'primaryObjective',
    label: 'Primary Objective',
    question:
      'What is your main objective with the cloud (e.g., reduce costs, improve performance...)?',
    type: 'text',
    required: true,
    examples: [
      'Reduce my storage costs',
      'Improve data access speed',
      'Enhance data security',
      'Scale storage capacity',
    ],
    clarificationExamples: [
      'A bit of everything → Can you specify your main priority?',
      "I don't know → Ok, we'll skip this but precision will be reduced.",
    ],
  },
  cloudProvider: {
    key: 'cloudProvider',
    label: 'Cloud Provider',
    question: 'Which cloud provider do you use (AWS, GCP, Azure, Storj...)?',
    type: 'text',
    required: true,
    examples: ['GCP', 'AWS', 'Azure', 'Storj', 'Multiple providers'],
    clarificationExamples: [
      'I think Google, or maybe Azure → clarify_request',
      "I don't know → skip_slot + warning",
    ],
  },
  monthlyBudget: {
    key: 'monthlyBudget',
    label: 'Monthly Budget',
    question: 'What is your approximate monthly cloud budget (USD)?',
    type: 'text',
    required: true,
    examples: ['Around $300', 'Less than $100', '$1000-5000', 'More than $10000'],
    clarificationExamples: [
      'Not huge → Can you give me a rough estimate?',
      "I don't know → skip_slot",
    ],
  },
  storageProvider: {
    key: 'storageProvider',
    label: 'Current Storage Provider',
    question: 'What storage solutions are you currently using?',
    type: 'text',
    required: false,
    examples: [
      'AWS S3',
      'Google Cloud Storage',
      'Azure Blob',
      'On-premise storage',
      'Multiple providers',
    ],
  },
  dataVolume: {
    key: 'dataVolume',
    label: 'Data Volume',
    question: 'What is your approximate data storage volume?',
    type: 'text',
    required: true,
    examples: ['Less than 1TB', '1TB-10TB', '10TB-100TB', 'More than 100TB'],
  },
  accessFrequency: {
    key: 'accessFrequency',
    label: 'Data Access Frequency',
    question: 'How frequently do you access your stored data?',
    type: 'text',
    required: false,
    examples: ['Daily access', 'Weekly access', 'Monthly access', 'Archive (rarely accessed)'],
  },
  currentIssues: {
    key: 'currentIssues',
    label: 'Current Challenges',
    question: 'What are your main challenges with your current storage setup?',
    type: 'text',
    required: false,
    examples: [
      'High costs',
      'Slow access times',
      'Data security concerns',
      'Scalability issues',
      'Complex management',
    ],
  },
  complianceRequirements: {
    key: 'complianceRequirements',
    label: 'Compliance Requirements',
    question: 'Do you have specific compliance or regulatory requirements?',
    type: 'text',
    required: false,
    examples: ['GDPR', 'HIPAA', 'SOC 2', 'ISO 27001', 'No specific requirements'],
  },
} as const;

// Available Gemini functions
export const GeminiFunctions = {
  [FunctionTypes.ASK_FOR_DATA]: {
    name: FunctionTypes.ASK_FOR_DATA,
    description: 'Ask for specific information from the user',
    parameters: {
      slotKey: 'string',
      question: 'string',
      context: 'string',
    },
  },
  [FunctionTypes.CLARIFY_REQUEST]: {
    name: FunctionTypes.CLARIFY_REQUEST,
    description: 'Ask for clarification on an ambiguous response',
    parameters: {
      originalQuestion: 'string',
      clarification: 'string',
    },
  },
  [FunctionTypes.SKIP_SLOT]: {
    name: FunctionTypes.SKIP_SLOT,
    description: "Skip a slot when user doesn't know",
    parameters: {
      slotKey: 'string',
      warning: 'string',
    },
  },
  [FunctionTypes.RECOMMENDATION]: {
    name: FunctionTypes.RECOMMENDATION,
    description: 'Provide final recommendation',
    parameters: {
      recommendation: 'string',
      confidence: 'string',
    },
  },
  [FunctionTypes.LIMIT_REACHED]: {
    name: FunctionTypes.LIMIT_REACHED,
    description: 'Inform that usage limit is reached',
    parameters: {
      nextAvailableDate: 'string',
    },
  },
} as const;

// Slot utilities
export const SlotUtils = {
  // Get next empty slot
  getNextEmptySlot: (currentSlots: Record<string, string>) => {
    const slotKeys = Object.keys(SlotSchema);
    for (const key of slotKeys) {
      if (!currentSlots[key]) {
        return key;
      }
    }
    return null; // All slots are filled
  },

  // Validate slot value
  validateSlotValue: (slotKey: string, value: string) => {
    if (!value || !value.trim()) return false;

    const slot = SlotSchema[slotKey as keyof typeof SlotSchema];
    if (!slot) return false;

    // Basic validation - can be extended based on type
    return value.trim().length > 0;
  },

  // Get completion percentage
  getCompletionPercentage: (currentSlots: Record<string, string>) => {
    const totalSlots = Object.keys(SlotSchema).length;
    const filledSlots = Object.keys(currentSlots).filter((key) => currentSlots[key]).length;
    return Math.round((filledSlots / totalSlots) * 100);
  },

  // Check if all required slots are filled
  areRequiredSlotsFilled: (currentSlots: Record<string, string>) => {
    const requiredSlots = Object.keys(SlotSchema).filter(
      (key) => SlotSchema[key as keyof typeof SlotSchema].required
    );
    return requiredSlots.every((key) => currentSlots[key]);
  },
};

// Usage limit management (1 recommendation per month for demo)
export const UsageLimit = {
  STORAGE_KEY: 'flashback_chat_usage_limit',

  // Check if limit is reached
  hasReachedLimit: () => {
    try {
      const stored = localStorage.getItem(UsageLimit.STORAGE_KEY);
      if (!stored) return false;

      const data = JSON.parse(stored);
      const now = new Date();
      const lastUsage = new Date(data.lastRecommendation);

      // Check if it's the same month
      return (
        now.getMonth() === lastUsage.getMonth() && now.getFullYear() === lastUsage.getFullYear()
      );
    } catch (error) {
      return false;
    }
  },

  // Record a new recommendation
  recordRecommendation: () => {
    try {
      const data = {
        lastRecommendation: new Date().toISOString(),
        count: 1,
      };
      localStorage.setItem(UsageLimit.STORAGE_KEY, JSON.stringify(data));
    } catch (error) { /* Ignore errors */ }
  },

  // Get next available date
  getRemainingTime: () => {
    try {
      const stored = localStorage.getItem(UsageLimit.STORAGE_KEY);
      if (!stored) return null;

      const data = JSON.parse(stored);
      const lastUsage = new Date(data.lastRecommendation);
      const nextMonth = new Date(lastUsage.getFullYear(), lastUsage.getMonth() + 1, 1);

      return nextMonth;
    } catch (error) {
      return null;
    }
  },

  // Reset limit (for testing)
  reset: () => {
    try {
      localStorage.removeItem(UsageLimit.STORAGE_KEY);
    } catch (error) { /* Ignore errors */ }
  },
};

// Create system prompt for Gemini
export const createSystemPrompt = (
  currentSlots: Record<string, string>,
  expectedSlotKey: string | null,
  options?: { deep?: boolean }
) => {
  const filledSlots = Object.keys(currentSlots).filter((key) => currentSlots[key]);
  const nextSlot = expectedSlotKey ? SlotSchema[expectedSlotKey as keyof typeof SlotSchema] : null;

  const deepMode = options?.deep === true;

  return `AI ROLE (Flashgate Dashboard In‑App Assistant):
You are the in‑app assistant embedded in the Flashgate Dashboard website. Your scope is limited to helping users use and understand this product and its features. You can:
- Guide users through this UI (navigation, buttons, tabs, forms).
- Explain how to use the cost optimization assistant and interpret results.
- Help with account, quotas, business vs individual, and usage limits messaging.
- Help export/download (e.g., PDF report) and contact support from within the app.

Out‑of‑scope: Do NOT answer general questions unrelated to this app or provide generic cloud consulting beyond what’s needed to use Flashgate’s optimization features. If asked out of scope, politely decline and propose relevant in‑app actions.

SLOT COLLECTION PROCESS:
Each slot corresponds to a predefined question. Follow this pattern for every question:
1. CLEAR RESPONSE HANDLING:
   - Bot: "What is your main objective with the cloud (e.g., reduce costs, improve performance...)?" 
   - Clear answer → "Reduce my storage costs" → slot primaryObjective = "cost_optimization"
   - Move to next question

2. AMBIGUOUS RESPONSE HANDLING:
   - Ambiguous answer → "A bit of everything" → call clarify_request ("Can you specify your main priority?")
   - Wait for clarification before proceeding

3. UNKNOWN RESPONSE HANDLING:
   - "I don't know" → call skip_slot ("Ok, we'll skip this but precision will be reduced.")
   - Move to next question with warning

EXAMPLE QUESTION FLOW:
- Bot: "What is your main objective with the cloud (e.g., reduce costs, improve performance...)?" 
- Bot: "Which cloud provider do you use (AWS, GCP, Azure, Storj...)?" 
- Bot: "What is your approximate monthly cloud budget (USD)?" 
- Continue with other defined questions in instructions.json

IMPORTANT RULES:
1. Stay within Flashgate Dashboard context. If out of scope → briefly refuse and suggest relevant actions in the app.
2. Ask ONLY ONE question at a time.
3. Always follow the pattern: fill slot / clarify / skip.
4. If the answer is clear, move to the next slot.
5. If the user says "I don't know", use skip_slot and add warning.
6. When all slots are processed, provide a final recommendation with concrete, product-relevant optimization advice.
7. Be natural and conversational and concise.
8. Prefer advice that the user can apply inside this product; avoid generic, non-actionable content.
${
  deepMode
    ? `
DEEP ANALYSIS MODE:
- Ask more detailed sub-questions beyond required slots (e.g., regions, data lifecycle, object size distribution, egress patterns, retrieval SLAs, durability/availability needs, expected growth, multi-cloud constraints).
- Quantify trade-offs with rough estimates where possible (storage, egress, API operations) and outline lifecycle policies inside Flashgate.
- Produce a more comprehensive final plan (structured, sectioned) with numbered steps that can be executed inside Flashgate.
- Keep asking until you have the details to provide a substantially improved plan.
`
    : ''
}

SLOTS TO COLLECT:
${Object.entries(SlotSchema)
  .map(
    ([key, slot]) =>
      `- ${slot.label} (${key}): ${slot.question} ${slot.required ? '[REQUIRED]' : '[OPTIONAL]'}`
  )
  .join('\n')}

CURRENT STATE:
- Filled slots: ${
    filledSlots.length > 0
      ? filledSlots
          .map((key) => `${SlotSchema[key as keyof typeof SlotSchema].label}: ${currentSlots[key]}`)
          .join(', ')
      : 'None'
  }
- Next expected slot: ${nextSlot ? nextSlot.label : 'All slots are processed'}

${
  nextSlot
    ? `NEXT QUESTION TO ASK: ${nextSlot.question}`
    : deepMode
    ? 'ASK FOR FURTHER DETAILS BEFORE FINAL RECOMMENDATION'
    : 'READY FOR FINAL RECOMMENDATION'
}

FINAL RECOMMENDATION GUIDELINES (Product‑Oriented):
When providing the final recommendation, keep it actionable within Flashgate:
- Analyze their provider/budget/objectives.
- Provide concrete steps they can follow with Flashgate’s features (e.g., selecting tiers, lifecycle policies, storage class choices) and, where needed, brief cloud actions.
- Include trade‑offs (cost vs performance vs reliability) succinctly.
- Keep focus on how the user can proceed within this app (and export PDF if helpful).

Respond in a natural and engaging way as a true cloud optimization expert.`;
};

// Message interface
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  functionCall?: {
    name: string;
    parameters: Record<string, any>;
  };
  isError?: boolean;
}

// Utility function to create a chat message
export const createChatMessage = (
  role: 'user' | 'assistant',
  content: string,
  functionCall: { name: string; parameters: Record<string, any> } | null = null,
  metadata: Record<string, any> = {}
): ChatMessage => {
  return {
    id: Date.now() + Math.random().toString(),
    role,
    content,
    timestamp: new Date().toISOString(),
    functionCall: functionCall || undefined,
    ...metadata,
  };
};
